import { useEffect, useRef } from "react";
import { N, rng, key, inB, pickColor, groups, blastCells, swapValid, findHint, reshuffle, swapG } from "@/lib/candyEngine";
import { sprite, PALETTE } from "@/lib/candySprites";
import * as sfx from "@/lib/candySfx";

const COMBO = ["", "", "Süß!", "Köstlich!", "Zauberhaft!", "Himmlisch!", "Traumhaft!"];
const ease = (p) => (p < 0.5 ? 4 * p * p * p : 1 - (-2 * p + 2) ** 3 / 2);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
let uid = 1;

function makeGame(level, demo) {
  const rand = rng((level.seed || 1) + Math.floor(Math.random() * 1e6));
  const g = Array.from({ length: N }, () => Array(N).fill(null));
  for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) g[y][x] = { c: pickColor(g, y, x, level.colors, rand), sp: null };
  if (!findHint(g)) reshuffle(g, rand);
  for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) Object.assign(g[y][x], { id: uid++, dx: x, dy: y - N - 1.5 - x * 0.35 - (N - y) * 0.08, vy: 0, land: 0, pop: 0 });
  return {
    level, demo, rand, g, ice: new Set((level.ice || []).map(([y, x]) => key(y, x))), dying: [], waiters: [],
    moves: level.moves, score: 0, collected: {}, busy: false, over: false, sel: null, down: null, hint: null, lastInput: performance.now() + 1200,
    fx: { parts: [], beams: [], rings: [], texts: [], rays: [] }, shake: { t0: 0, amp: 0 }, cascade: 0, size: 0, cell: 0, pad: 0,
  };
}

export function CandyBoard({ level, demo = false, onHud, onDone, onCombo, onBoost, onOutOfMoves, onHammerDone, ctrl, testId = "candy-board", paused = false }) {
  const wrapRef = useRef(null);
  const cvRef = useRef(null);
  const S = useRef(null);
  const cb = useRef({});
  cb.current = { onHud, onDone, onCombo, onBoost, onOutOfMoves, onHammerDone, paused };

  useEffect(() => {
    const s = (S.current = makeGame(level, demo));
    const cv = cvRef.current, ctx = cv.getContext("2d");
    let raf, last = performance.now(), bg = null, alive = true;
    const hud = () => cb.current.onHud?.({ moves: s.moves, score: s.score, collected: { ...s.collected }, iceLeft: s.ice.size });
    const combo = (t) => !demo && cb.current.onCombo?.(t);
    hud();

    const layout = () => {
      const w = wrapRef.current?.clientWidth || 360;
      const size = Math.min(w, 540), dpr = Math.min(2.5, window.devicePixelRatio || 1);
      s.size = size; s.dpr = dpr; s.pad = size * 0.03; s.cell = (size - s.pad * 2) / N;
      cv.width = Math.round(size * dpr); cv.height = Math.round(size * dpr); cv.style.width = cv.style.height = `${size}px`;
      bg = document.createElement("canvas"); bg.width = cv.width; bg.height = cv.height;
      const b = bg.getContext("2d"); b.scale(dpr, dpr);
      const r = s.cell * 0.45;
      const fill = b.createLinearGradient(0, 0, 0, size);
      fill.addColorStop(0, "rgba(255,240,245,.11)"); fill.addColorStop(1, "rgba(255,240,245,.04)");
      b.fillStyle = fill; b.beginPath(); b.roundRect(1, 1, size - 2, size - 2, r); b.fill();
      const edge = b.createLinearGradient(0, 0, size, size);
      edge.addColorStop(0, "rgba(255,226,170,.7)"); edge.addColorStop(0.5, "rgba(255,226,170,.12)"); edge.addColorStop(1, "rgba(255,226,170,.55)");
      b.strokeStyle = edge; b.lineWidth = 1.2; b.stroke();
      for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
        b.fillStyle = (x + y) % 2 ? "rgba(255,255,255,.035)" : "rgba(255,255,255,.075)";
        b.beginPath(); b.roundRect(s.pad + x * s.cell + 1.5, s.pad + y * s.cell + 1.5, s.cell - 3, s.cell - 3, s.cell * 0.22); b.fill();
      }
    };
    layout();
    const ro = new ResizeObserver(layout); ro.observe(wrapRef.current);

    const P = (y, x) => [s.pad + (x + 0.5) * s.cell, s.pad + (y + 0.5) * s.cell];
    const settle = () => new Promise((r) => s.waiters.push(r));

    const burst = (y, x, c) => {
      const [px, py] = P(y, x), pal = PALETTE[c] || PALETTE[3];
      for (let i = 0; i < 9; i++) {
        const a = Math.random() * Math.PI * 2, v = 0.06 + Math.random() * 0.22;
        s.fx.parts.push({ x: px, y: py, vx: Math.cos(a) * v, vy: Math.sin(a) * v - 0.08, life: 0, max: 450 + Math.random() * 450, col: i % 3 ? pal[1] : pal[0], r: s.cell * (0.04 + Math.random() * 0.07), star: i < 2 });
      }
    };
    const shards = (y, x) => {
      const [px, py] = P(y, x);
      for (let i = 0; i < 7; i++) {
        const a = Math.random() * Math.PI * 2, v = 0.05 + Math.random() * 0.16;
        s.fx.parts.push({ x: px, y: py, vx: Math.cos(a) * v, vy: Math.sin(a) * v - 0.06, life: 0, max: 600, col: "#e6f4ff", r: s.cell * 0.06, shard: true });
      }
    };
    const text = (y, x, t, big) => { const [px, py] = P(y, x); s.fx.texts.push({ x: px, y: py, t, life: 0, big }); };
    const shake = (amp) => { s.shake = { t0: performance.now(), amp }; navigator.vibrate?.(amp > 5 ? 30 : 12); };

    const gravity = () => {
      for (let x = 0; x < N; x++) {
        let w = N - 1;
        for (let y = N - 1; y >= 0; y--) if (s.g[y][x]) { const t = s.g[y][x]; s.g[y][x] = null; s.g[w][x] = t; w--; }
        let k = 0;
        for (let y = w; y >= 0; y--) {
          k++;
          s.g[y][x] = { id: uid++, c: pickColor(s.g, y, x, s.level.colors, s.rand), sp: null, dx: x, dy: -k - 0.3, vy: 0, land: 0, pop: 0 };
        }
      }
    };

    const clearSet = async (cells, { creates = [], hints = new Map(), skip = new Set(), mult = 1 } = {}) => {
      const createK = new Set(creates.map(({ y, x }) => key(y, x)));
      const queue = [...cells.values()], act = new Set(skip);
      let acts = 0;
      while (queue.length) {
        const [y, x] = queue.pop(), k = key(y, x), t = s.g[y][x];
        if (!t || !t.sp || act.has(k) || createK.has(k)) continue;
        act.add(k); acts++;
        const [px, py] = P(y, x), now = performance.now();
        if (t.sp === "h" || t.sp === "v") { s.fx.beams.push({ dir: t.sp, y: py, x: px, t0: now, col: PALETTE[t.c][1] }); sfx.czStripe(); }
        if (t.sp === "w") { s.fx.rings.push({ x: px, y: py, t0: now, r: s.cell * 2.2, col: "255,214,140" }); shake(6); sfx.czWrap(); }
        if (t.sp === "b") { s.fx.rings.push({ x: px, y: py, t0: now, r: s.size * 0.7, col: "255,240,255" }); shake(8); sfx.czBomb(); }
        const out = blastCells(s.g, y, x, t.sp, hints.get(k));
        if (t.sp === "b") out.forEach(([yy, xx]) => { const [tx, ty] = P(yy, xx); s.fx.rays.push({ x0: px, y0: py, x1: tx, y1: ty, t0: now }); });
        out.forEach(([yy, xx]) => { const kk = key(yy, xx); if (!cells.has(kk)) { cells.set(kk, [yy, xx]); queue.push([yy, xx]); } });
      }
      let n = 0, sy = 0, sx = 0, iceHit = false;
      const now = performance.now();
      cells.forEach(([y, x], k) => {
        const t = s.g[y][x];
        if (s.ice.has(k)) { s.ice.delete(k); shards(y, x); iceHit = true; }
        if (!t || createK.has(k)) return;
        t.die = now; s.dying.push({ t, y, x }); s.g[y][x] = null; n++; sy += y; sx += x;
        if (t.c >= 0) s.collected[t.c] = (s.collected[t.c] || 0) + 1;
        burst(y, x, t.c >= 0 ? t.c : 3);
      });
      if (iceHit) sfx.czIce();
      creates.forEach(({ y, x, c, sp }) => {
        const t = s.g[y][x]; if (!t) return;
        t.sp = sp; t.c = sp === "b" ? -1 : c; t.pop = now; s.fx.rings.push({ x: P(y, x)[0], y: P(y, x)[1], t0: now, r: s.cell * 0.9, col: "255,255,255" });
      });
      if (creates.length) { sfx.czCreate(); s.score += creates.length * 200; }
      const pts = Math.round(n * 20 * Math.max(1, s.cascade) * mult + acts * 60);
      s.score += pts;
      if (n) text(sy / n, sx / n, `+${pts}`, pts >= 600);
      hud();
      await sleep(210);
      gravity();
      await settle();
    };

    const cascade = async (prefer = []) => {
      for (;;) {
        const gs = groups(s.g, prefer);
        prefer = [];
        if (!gs.length) break;
        s.cascade++;
        sfx.czMatch(s.cascade);
        if (COMBO[Math.min(6, s.cascade)]) combo(COMBO[Math.min(6, s.cascade)]);
        const cells = new Map(), creates = [];
        gs.forEach((gr) => { gr.cells.forEach((v, k) => cells.set(k, v)); if (gr.special) creates.push({ y: gr.at[0], x: gr.at[1], c: gr.c, sp: gr.special }); });
        await clearSet(cells, { creates });
      }
      if (!findHint(s.g)) { combo("Neu gemischt"); sfx.czShuffle(); reshuffle(s.g, s.rand); s.g.flat().forEach((t) => { t.pop = performance.now(); }); await sleep(400); await cascade(); }
    };

    const comboSwap = (A, B, at, other) => {
      const [y, x] = at, cells = new Map(), add = (yy, xx) => inB(yy, xx) && cells.set(key(yy, xx), [yy, xx]);
      const skip = new Set([key(...at), key(...other)]);
      add(y, x); add(...other);
      const sps = [A.sp, B.sp];
      if (A.sp === "b" && B.sp === "b") { for (let i = 0; i < N; i++) for (let j = 0; j < N; j++) add(i, j); return { cells, skip, mult: 1.5, big: true }; }
      if (A.sp === "b" || B.sp === "b") {
        const bombAt = A.sp === "b" ? at : other, O = A.sp === "b" ? B : A, [by, bx] = bombAt;
        const [px, py] = P(by, bx), now = performance.now();
        s.g.flat().forEach((t, i) => {
          if (t?.c !== O.c) return;
          const yy = Math.floor(i / N), xx = i % N;
          if (O.sp && O.sp !== "b") { t.sp = O.sp === "w" ? "w" : s.rand() < 0.5 ? "h" : "v"; t.pop = now; skip.delete(key(yy, xx)); }
          add(yy, xx); const [tx, ty] = P(yy, xx); s.fx.rays.push({ x0: px, y0: py, x1: tx, y1: ty, t0: now });
        });
        if (O.sp) skip.delete(key(...(A.sp === "b" ? other : at)));
        s.fx.rings.push({ x: px, y: py, t0: now, r: s.size * 0.8, col: "255,240,255" }); shake(9); sfx.czBomb();
        return { cells, skip, mult: 1.2, big: true, delay: O.sp ? 420 : 0 };
      }
      const st = sps.filter((v) => v === "h" || v === "v").length, w = sps.filter((v) => v === "w").length;
      const [px, py] = P(y, x), now = performance.now();
      if (st === 2) { for (let i = 0; i < N; i++) { add(y, i); add(i, x); } s.fx.beams.push({ dir: "h", y: py, x: px, t0: now, col: "#fff" }, { dir: "v", y: py, x: px, t0: now, col: "#fff" }); sfx.czStripe(); }
      else if (st === 1 && w === 1) { for (let d = -1; d <= 1; d++) for (let i = 0; i < N; i++) { add(y + d, i); add(i, x + d); } [-1, 0, 1].forEach((d) => s.fx.beams.push({ dir: "h", y: py + d * s.cell, x: px, t0: now, col: "#ffd68c", wide: true }, { dir: "v", y: py, x: px + d * s.cell, t0: now, col: "#ffd68c", wide: true })); shake(7); sfx.czStripe(); sfx.czWrap(); }
      else { for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) add(y + dy, x + dx); s.fx.rings.push({ x: px, y: py, t0: now, r: s.cell * 3.4, col: "255,214,140" }); shake(10); sfx.czWrap(); }
      return { cells, skip, mult: 1.3, big: true };
    };

    const goalsMet = () => {
      const L = s.level;
      if (L.kind === "collect") return Object.entries(L.collect).every(([c, n]) => (s.collected[c] || 0) >= n);
      if (L.kind === "ice") return s.ice.size === 0;
      return false;
    };

    const sugarCrush = async () => {
      combo("Zuckerrausch!");
      await sleep(500);
      const n = Math.min(s.moves, 12);
      for (let i = 0; i < n; i++) {
        const opts = [];
        s.g.forEach((row, y) => row.forEach((t, x) => t && !t.sp && opts.push([y, x, t])));
        if (!opts.length) break;
        const [y, x, t] = opts[Math.floor(s.rand() * opts.length)];
        t.sp = s.rand() < 0.5 ? "h" : "v"; t.pop = performance.now(); s.moves--; s.score += 400; text(y, x, "+400");
        sfx.czCrush(); hud(); await sleep(110);
      }
      for (let r = 0; r < 4; r++) {
        const cells = new Map();
        s.g.forEach((row, y) => row.forEach((t, x) => t?.sp && cells.set(key(y, x), [y, x])));
        if (!cells.size) break;
        s.cascade = 1; await clearSet(cells); await cascade();
      }
    };

    const endCheck = async () => {
      if (demo || s.over) return;
      const met = goalsMet();
      if (!(met || s.moves <= 0)) return;
      s.over = true;
      const won = s.level.kind === "score" ? s.score >= s.level.stars[0] : met;
      if (!won && cb.current.onOutOfMoves && (await cb.current.onOutOfMoves({ score: s.score, collected: { ...s.collected }, iceLeft: s.ice.size }))) {
        s.moves += 5; s.over = false; hud(); combo("+5 Züge"); sfx.czCreate(); s.lastInput = performance.now(); return;
      }
      const movesLeft = s.moves;
      if (won) { await sugarCrush(); sfx.czWin(); } else sfx.czFail();
      await sleep(400);
      cb.current.onDone?.({ won, score: s.score, movesLeft });
    };

    const trySwap = async (a, b) => {
      if (s.busy || s.over || cb.current.paused) return;
      const A = s.g[a[0]][a[1]], B = s.g[b[0]][b[1]];
      if (!A || !B) return;
      s.busy = true; s.sel = null; s.hint = null; s.lastInput = performance.now();
      const valid = swapValid(s.g, a, b), now = performance.now();
      A.tw = { fx: a[1], fy: a[0], tx: b[1], ty: b[0], t0: now, dur: 170 }; B.tw = { fx: b[1], fy: b[0], tx: a[1], ty: a[0], t0: now, dur: 170 };
      swapG(s.g, a, b); sfx.czSwap(); await settle();
      if (!valid) {
        const n2 = performance.now();
        A.tw = { fx: b[1], fy: b[0], tx: a[1], ty: a[0], t0: n2, dur: 170 }; B.tw = { fx: a[1], fy: a[0], tx: b[1], ty: b[0], t0: n2, dur: 170 };
        swapG(s.g, a, b); sfx.czInvalid(); await settle(); s.busy = false; return;
      }
      if (!demo) { s.moves--; hud(); }
      s.cascade = 0;
      if ((A.sp && B.sp) || A.sp === "b" || B.sp === "b") {
        const r = comboSwap(A, B, b, a);
        if (r.delay) await sleep(r.delay);
        s.cascade = 1; combo(r.big ? "Fantastisch!" : "");
        await clearSet(r.cells, { skip: r.skip, mult: r.mult });
        await cascade();
      } else await cascade([b, a]);
      s.busy = false;
      await endCheck();
      s.lastInput = performance.now();
    };

    const smash = async (c) => {
      s.busy = true;
      const ok = await cb.current.onBoost?.("hammer");
      cb.current.onHammerDone?.();
      if (!ok || !s.g[c[0]][c[1]]) { s.busy = false; return; }
      const [px, py] = P(...c);
      s.fx.rings.push({ x: px, y: py, t0: performance.now(), r: s.cell * 1.6, col: "255,214,140" }); shake(9); sfx.czWrap();
      s.cascade = 1;
      await clearSet(new Map([[key(...c), c]]));
      await cascade();
      s.busy = false; s.lastInput = performance.now();
      await endCheck();
    };
    if (ctrl) ctrl.current = {
      hammer: (on) => { s.hammer = on; s.sel = null; },
      shuffle: async () => {
        if (s.busy || s.over) return false;
        s.busy = true; sfx.czShuffle(); combo("Neu gemischt");
        reshuffle(s.g, s.rand); s.g.flat().forEach((t) => { if (t) t.pop = performance.now(); });
        await sleep(450); s.cascade = 0; await cascade();
        s.busy = false; s.lastInput = performance.now(); return true;
      },
      busy: () => s.busy || s.over,
    };

    const cellAt = (e) => {
      const r = cv.getBoundingClientRect();
      const x = Math.floor((e.clientX - r.left - s.pad) / s.cell), y = Math.floor((e.clientY - r.top - s.pad) / s.cell);
      return inB(y, x) ? [y, x] : null;
    };
    const down = (e) => {
      if (demo) return;
      const c = cellAt(e); if (!c) return;
      cv.setPointerCapture?.(e.pointerId);
      s.down = { c, x: e.clientX, y: e.clientY }; s.lastInput = performance.now(); s.hint = null;
    };
    const move = (e) => {
      if (!s.down) return;
      const dx = e.clientX - s.down.x, dy = e.clientY - s.down.y;
      if (s.hammer || Math.hypot(dx, dy) < s.cell * 0.32) return;
      const [y, x] = s.down.c, t = Math.abs(dx) > Math.abs(dy) ? [y, x + Math.sign(dx)] : [y + Math.sign(dy), x];
      s.down = null;
      if (inB(...t)) trySwap([y, x], t);
    };
    const up = () => {
      if (!s.down) return;
      const c = s.down.c; s.down = null;
      if (s.hammer) { s.hammer = false; if (!s.busy && !s.over) smash(c); else cb.current.onHammerDone?.(); return; }
      if (s.sel && Math.abs(s.sel[0] - c[0]) + Math.abs(s.sel[1] - c[1]) === 1) { const a = s.sel; s.sel = null; trySwap(a, c); return; }
      s.sel = s.sel && s.sel[0] === c[0] && s.sel[1] === c[1] ? null : c; sfx.czTap();
    };
    cv.addEventListener("pointerdown", down); cv.addEventListener("pointermove", move); window.addEventListener("pointerup", up);

    const drawTile = (t, x, y, sc = 1, alpha = 1, rot = 0) => {
      const c = s.cell, px = Math.max(16, Math.round(c * s.dpr));
      const img = sprite(t.c < 0 ? 0 : t.c, t.sp, px);
      const cx = s.pad + (x + 0.5) * c, cy = s.pad + (y + 0.5) * c;
      ctx.save(); ctx.globalAlpha = alpha; ctx.translate(cx, cy); if (rot) ctx.rotate(rot);
      const sx = sc * (1 + 0.1 * t.land), sy = sc * (1 - 0.12 * t.land);
      ctx.scale(sx, sy); ctx.drawImage(img, -c / 2, -c / 2 + c * 0.06 * t.land, c, c); ctx.restore();
    };

    const frame = (now) => {
      if (!alive) return;
      const dt = Math.min(34, now - last); last = now;
      const c = s.cell;
      let moving = false;
      for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
        const t = s.g[y][x]; if (!t) continue;
        if (t.tw) {
          const p = Math.min(1, (now - t.tw.t0) / t.tw.dur), e = ease(p);
          t.dx = t.tw.fx + (t.tw.tx - t.tw.fx) * e; t.dy = t.tw.fy + (t.tw.ty - t.tw.fy) * e;
          if (p >= 1) { t.tw = null; t.dx = x; t.dy = y; } else moving = true;
        } else if (t.dy < y) {
          t.vy += 0.00011 * dt; t.dy = Math.min(y, t.dy + t.vy * dt); t.dx = x; moving = true;
          if (t.dy >= y) { t.land = Math.min(1, t.vy * 90); t.vy = 0; }
        } else { t.dy = y; t.dx = x; t.vy = 0; }
        if (t.land > 0) t.land = Math.max(0, t.land - dt / 170);
      }
      s.dying = s.dying.filter((d) => now - d.t.die < 260);
      if (!moving && !s.dying.length && s.waiters.length) { const w = s.waiters; s.waiters = []; w.forEach((r) => r()); }

      if (!s.busy && !s.over && !cb.current.paused) {
        const idle = now - s.lastInput;
        if (demo && idle > 1400) { const h = findHint(s.g); if (h) trySwap(...h); s.lastInput = now; }
        else if (!demo && idle > 5500 && !s.hint) s.hint = findHint(s.g);
      }

      ctx.setTransform(s.dpr, 0, 0, s.dpr, 0, 0);
      ctx.clearRect(0, 0, s.size, s.size);
      const sh = s.shake, sp = (now - sh.t0) / 320;
      if (sp < 1) ctx.translate((Math.random() - 0.5) * sh.amp * (1 - sp), (Math.random() - 0.5) * sh.amp * (1 - sp));
      if (bg) ctx.drawImage(bg, 0, 0, s.size, s.size);
      s.ice.forEach((k) => {
        const y = Math.floor(k / N), x = k % N, ix = s.pad + x * c + c * 0.07, iy = s.pad + y * c + c * 0.07, w = c * 0.86;
        const gi = ctx.createLinearGradient(ix, iy, ix + w, iy + w);
        gi.addColorStop(0, "rgba(235,246,255,.7)"); gi.addColorStop(1, "rgba(150,195,255,.35)");
        ctx.fillStyle = gi; ctx.beginPath(); ctx.roundRect(ix, iy, w, w, c * 0.2); ctx.fill();
        ctx.strokeStyle = "rgba(255,255,255,.75)"; ctx.lineWidth = 1.2; ctx.stroke();
        ctx.strokeStyle = "rgba(255,255,255,.45)"; ctx.beginPath(); ctx.moveTo(ix + w * 0.18, iy + w * 0.55); ctx.lineTo(ix + w * 0.55, iy + w * 0.18); ctx.stroke();
      });
      ctx.save(); ctx.beginPath(); ctx.rect(0, s.pad * 0.5, s.size, s.size); ctx.clip();
      const hk = s.hint ? new Set(s.hint.map(([y, x]) => key(y, x))) : null;
      for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
        const t = s.g[y][x]; if (!t) continue;
        let sc = 1;
        if (t.pop) { const p = (now - t.pop) / 320; if (p < 1) sc = 0.4 + 0.6 * ease(p) + Math.sin(p * Math.PI) * 0.18; else t.pop = 0; }
        if (hk?.has(key(y, x))) sc *= 1 + 0.07 * Math.sin(now / 140);
        const isSel = s.sel && s.sel[0] === y && s.sel[1] === x;
        if (isSel) {
          const [px, py] = P(y, x); ctx.save(); ctx.strokeStyle = "rgba(255,228,170,.95)"; ctx.lineWidth = 2; ctx.shadowColor = "#ffd68c"; ctx.shadowBlur = 14;
          ctx.beginPath(); ctx.roundRect(px - c * 0.46, py - c * 0.46, c * 0.92, c * 0.92, c * 0.24); ctx.stroke(); ctx.restore();
          sc *= 1.06 + 0.03 * Math.sin(now / 120);
        }
        if (s.hammer) sc *= 1 + 0.035 * Math.sin(now / 160 + x + y);
        drawTile(t, t.dx, t.dy, sc);
      }
      if (s.hammer) { ctx.fillStyle = `rgba(255,214,140,${0.05 + 0.04 * Math.sin(now / 200)})`; ctx.fillRect(0, 0, s.size, s.size); }
      ctx.restore();
      s.dying.forEach(({ t, y, x }) => { const p = (now - t.die) / 260; drawTile(t, x, y, 1 + 0.35 * p, Math.max(0, 1 - p * p), p * 0.3); });

      ctx.save(); ctx.globalCompositeOperation = "lighter";
      s.fx.beams = s.fx.beams.filter((b) => {
        const p = (now - b.t0) / 420; if (p >= 1) return false;
        const w = c * (b.wide ? 1.1 : 0.8) * (1 - p * 0.7), a = 1 - p;
        const gr = b.dir === "h" ? ctx.createLinearGradient(0, b.y - w / 2, 0, b.y + w / 2) : ctx.createLinearGradient(b.x - w / 2, 0, b.x + w / 2, 0);
        gr.addColorStop(0, "rgba(255,255,255,0)"); gr.addColorStop(0.5, `rgba(255,255,255,${a})`); gr.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = gr;
        if (b.dir === "h") ctx.fillRect(0, b.y - w / 2, s.size, w); else ctx.fillRect(b.x - w / 2, 0, w, s.size);
        ctx.globalAlpha = a * 0.6; ctx.fillStyle = b.col;
        if (b.dir === "h") ctx.fillRect(0, b.y - w * 0.12, s.size, w * 0.24); else ctx.fillRect(b.x - w * 0.12, 0, w * 0.24, s.size);
        ctx.globalAlpha = 1; return true;
      });
      s.fx.rays = s.fx.rays.filter((r) => {
        const p = (now - r.t0) / 500; if (p >= 1) return false;
        ctx.strokeStyle = `rgba(255,236,255,${(1 - p) * 0.8})`; ctx.lineWidth = 2.2 * (1 - p) + 0.5;
        ctx.beginPath(); ctx.moveTo(r.x0, r.y0); ctx.lineTo(r.x0 + (r.x1 - r.x0) * Math.min(1, p * 3), r.y0 + (r.y1 - r.y0) * Math.min(1, p * 3)); ctx.stroke(); return true;
      });
      s.fx.rings = s.fx.rings.filter((r) => {
        const p = (now - r.t0) / 520; if (p >= 1) return false;
        ctx.strokeStyle = `rgba(${r.col},${(1 - p) * 0.9})`; ctx.lineWidth = 3 * (1 - p) + 0.5;
        ctx.beginPath(); ctx.arc(r.x, r.y, r.r * ease(p), 0, Math.PI * 2); ctx.stroke(); return true;
      });
      s.fx.parts = s.fx.parts.filter((q) => {
        q.life += dt; if (q.life >= q.max) return false;
        q.x += q.vx * dt; q.y += q.vy * dt; q.vy += 0.0005 * dt; q.vx *= 0.985;
        const a = 1 - q.life / q.max; ctx.globalAlpha = a; ctx.fillStyle = q.col;
        if (q.star) { ctx.save(); ctx.translate(q.x, q.y); ctx.rotate(q.life / 120); const r = q.r * 1.8; ctx.beginPath(); for (let i = 0; i < 8; i++) { const rr = i % 2 ? r * 0.25 : r; ctx.lineTo(Math.cos((i * Math.PI) / 4) * rr, Math.sin((i * Math.PI) / 4) * rr); } ctx.fillStyle = "#fff"; ctx.fill(); ctx.restore(); }
        else if (q.shard) { ctx.fillRect(q.x, q.y, q.r * 1.4, q.r * 0.7); }
        else { ctx.beginPath(); ctx.arc(q.x, q.y, q.r * (0.5 + a * 0.5), 0, Math.PI * 2); ctx.fill(); }
        return true;
      });
      ctx.globalAlpha = 1; ctx.restore();
      s.fx.texts = s.fx.texts.filter((t) => {
        t.life += dt; const p = t.life / 900; if (p >= 1) return false;
        const fs = c * (t.big ? 0.62 : 0.44) * (p < 0.15 ? 0.6 + p * 2.7 : 1);
        ctx.save(); ctx.globalAlpha = p > 0.7 ? (1 - p) / 0.3 : 1; ctx.font = `italic 800 ${fs}px "Playfair Display", Georgia, serif`; ctx.textAlign = "center";
        const y = t.y - p * c * 0.9;
        ctx.lineWidth = fs * 0.16; ctx.strokeStyle = "rgba(40,6,24,.85)"; ctx.strokeText(t.t, t.x, y);
        const gt = ctx.createLinearGradient(0, y - fs, 0, y); gt.addColorStop(0, "#fff7dc"); gt.addColorStop(1, "#f2b54a");
        ctx.fillStyle = gt; ctx.fillText(t.t, t.x, y); ctx.restore(); return true;
      });
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => { alive = false; cancelAnimationFrame(raf); ro.disconnect(); cv.removeEventListener("pointerdown", down); cv.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
  }, [level, demo]);

  return (
    <div ref={wrapRef} className="w-full flex justify-center select-none" data-testid={testId}>
      <canvas ref={cvRef} className="touch-none block" style={{ touchAction: "none" }} />
    </div>
  );
}
